import React, { createContext, useState, useEffect, useCallback, useRef } from "react";
import { getUser, getSession, logoutSession } from "../services";
import { useDisclosure } from '@chakra-ui/react';
import UpgradeSuccessModal from '../components/UI/UpgradeSuccessModal';

const AuthContext = createContext({
  token: '',
  isLoggedIn: false,
  login: () => {},
  logout: () => {},
  isRedirected: false,
  redirection: () => {},
  userData: null,
  userTier: 'free',
  isFreeTier: true,
  isVipPlus: false,
  closeUpgradeSuccess: () => {},
  patchUserData: () => {}
});

// Add this helper function just before the AuthContextProvider to validate user data
const isValidUserData = (data) => {
  // First check if data exists
  if (!data) return false;
  
  // Check for direct properties (_id or userId is required)
  if (data._id || data.userId) return true;
  
  // Check for nested data structure (response.data.data)
  if (data.data && typeof data.data === 'object' && (data.data._id || data.data.userId)) return true;
  
  // No valid user data found
  return false;
};

// Extract user data from potentially nested response
const extractUserData = (response) => {
  if (!response) return null;
  
  // Direct user data
  if (response._id || response.userId) {
    // Ensure image property is properly handled
    if (response.image === undefined) {
      response.image = null;
    }
    return response;
  }
  
  // Check if data is in the data property (nested structure)
  if (response.data && typeof response.data === 'object' && (response.data._id || response.data.userId)) {
    // Ensure image property is properly handled
    if (response.data.image === undefined) {
      response.data.image = null;
    }
    return response.data;
  }
  
  // No valid user data found
  return null;
};

// Set once this browser has been seen to hold a working shared-session cookie
// (see the session probe below). While it is set, a definitive "no session"
// answer from the API means the user signed out on the other site, and the
// local copy of the token is dropped to match. Never set when the cookie
// cannot work (API on a different domain), so that case keeps the old
// local-only behaviour.
const COOKIE_SESSION_FLAG = 'mm_cookie_session';

// Derive tier string from user data object — single source of truth
const getUserTier = (data) => {
  if (!data) return 'free';
  if (data.isVipPlus) return 'vip_plus';
  if (data.isVerified) return 'paid';
  return 'free';
};

export const AuthContextProvider = ({ children }) => {
  // Initialize state with values from storage
  const [token, setToken] = useState(() => {
    // Check sessionStorage first (current session), then localStorage (remember me)
    const sessionToken = sessionStorage.getItem('token');
    const sessionExpiry = sessionStorage.getItem('expiresIn');
    
    if (sessionToken && sessionExpiry) {
      const now = new Date().getTime();
      if (now < parseInt(sessionExpiry)) {
        return sessionToken;
      }
      // Session expired, clear it
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('expiresIn');
      sessionStorage.removeItem('userData');
    }
    
    // Check localStorage (remember me)
    const localToken = localStorage.getItem('token');
    const localExpiry = localStorage.getItem('expiresIn');
    
    if (localToken && localExpiry) {
      const now = new Date().getTime();
      if (now < parseInt(localExpiry)) {
        // Copy to sessionStorage for current session
        sessionStorage.setItem('token', localToken);
        sessionStorage.setItem('expiresIn', localExpiry);
        return localToken;
      }
      // Remember me expired, clear it
      localStorage.removeItem('token');
      localStorage.removeItem('expiresIn');
      localStorage.removeItem('userData');
    }
    
    return null;
  });
  
  const [userData, setUserData] = useState(() => {
    // Prioritize sessionStorage, then localStorage
    const storedUserData = sessionStorage.getItem('userData') || localStorage.getItem('userData');
    try {
      return storedUserData ? JSON.parse(storedUserData) : null;
    } catch (error) {
      console.error('Error parsing stored user data:', error);
      return null;
    }
  });
  
  const [isRedirected, setIsRedirected] = useState(null);
  const previousTierRef = useRef(null);
  const { isOpen: isUpgradeSuccessOpen, onOpen: onOpenUpgradeSuccess, onClose: onCloseUpgradeSuccess } = useDisclosure();

  // Derived state
  const userTier = userData?.isVipPlus ? 'vip_plus' : userData?.isVerified ? 'paid' : 'free';
  // Administrators see everything, as the API's tier rule has it, whatever their own flags say.
  const isFreeTier = userTier === 'free' && userData?.userRole !== 'administrator';
  const isVipPlus = userTier === 'vip_plus';
  const userIsLoggedIn = !!token && !!userData;
  
  // Clear all auth data on logout
  const logoutHandler = useCallback(() => {
    console.log("Logging out, clearing auth data");
    setUserData(null);
    setToken(null);
    localStorage.removeItem('token');
    localStorage.removeItem('userData');
    localStorage.removeItem('expiresIn');
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('expiresIn');
    sessionStorage.removeItem('userData');
    localStorage.removeItem(COOKIE_SESSION_FLAG);
    // Drop the shared cookie too, or the next page load - here or on the
    // marketing site - would quietly sign the user straight back in.
    logoutSession();
  }, []);

  // Handle login and save token + user data
  const loginHandler = useCallback((authData, rememberMe = false) => {
    if (!authData || !authData.token) {
      console.error('Invalid auth data received:', authData);
      return;
    }
    
    console.log("Authentication successful, remember me:", rememberMe);
    // Any copy of the user record held now is from an earlier visit. Drop it, so the
    // user counts as signed in only once the record fetched below has arrived.
    setUserData(null);
    sessionStorage.removeItem('userData');
    localStorage.removeItem('userData');

    // First set the token - this enables authenticated API calls
    setToken(authData.token);
    
    // Always store in sessionStorage for the current session
    sessionStorage.setItem('token', authData.token);
    sessionStorage.setItem(
      "expiresIn",
      (new Date().getTime() + 3600000 * 48).toString() // 48 hours expiry
    );
    
    // Only store in localStorage if "remember me" is checked (for 30 days persistence)
    if (rememberMe) {
      localStorage.setItem('token', authData.token);
      localStorage.setItem(
        "expiresIn",
        (new Date().getTime() + 3600000 * 24 * 30).toString() // 30 days expiry
      );
    } else {
      // Clear localStorage if remember me is not checked
      localStorage.removeItem('token');
      localStorage.removeItem('expiresIn');
    }
    
    // Learn whether the shared cookie the API just set actually reached this
    // browser (it only can when the API is on an importjdmmagic.com host). A
    // 200 from the cookie-only probe proves it; remember that so a later
    // sign-out on the marketing site can be honoured here.
    getSession().then((probe) => {
      if (probe.status === 200) localStorage.setItem(COOKIE_SESSION_FLAG, '1');
    });

    // Then fetch the user data with explicit authentication
    const fetchUserData = async () => {
      try {
        console.log("Fetching user data after login");
        
        // Try up to 3 times with a delay to allow backend to process the token
        let attempts = 0;
        const maxAttempts = 3;
        let response;
        
        while (attempts < maxAttempts) {
          attempts++;
          
          try {
            // Wait a bit before trying if not the first attempt
            if (attempts > 1) {
              await new Promise(resolve => setTimeout(resolve, 500));
            }
            
            response = await getUser();
            console.log("User data response:", response);

            // Always the /auth/me record, never authData: the login and session responses
            // have no deposit, favorites or saved searches.
            if (response && response.status === 200 && isValidUserData(response.data)) {
              // Extract the user data using the helper
              const userData = extractUserData(response.data);
              console.log("User data received:", userData);
              previousTierRef.current = getUserTier(userData);
              
              // Update state and storage with user data
              setUserData(userData);
              const userDataStr = JSON.stringify(userData);
              sessionStorage.setItem('userData', userDataStr);
              
              // Only store in localStorage if remember me was checked
              if (rememberMe) {
                localStorage.setItem('userData', userDataStr);
              }
              
              break; // Exit the retry loop on success
            } else {
              console.warn(`Attempt ${attempts}: Invalid user data structure:`, response);
              
              // Last attempt failed, clear auth and notify
              if (attempts === maxAttempts) {
                console.error("Failed to get valid user data after multiple attempts");
                logoutHandler();
              }
            }
          } catch (attemptError) {
            console.warn(`Attempt ${attempts} failed:`, attemptError);
            
            // Last attempt failed with error, clear auth and notify
            if (attempts === maxAttempts) {
              console.error("Failed to fetch user data after multiple attempts:", attemptError);
              logoutHandler();
            }
          }
        }
      } catch (error) {
        console.error('Failed to fetch user data:', error);
        logoutHandler();
      }
    };
    
    return fetchUserData();
  }, [logoutHandler]);

  const redirectionHandler = useCallback((val) => {
    setIsRedirected(val);
  }, []);

  // Check for user tier changes and show success notification when upgraded
  useEffect(() => {
    if (userData && previousTierRef.current !== null) {
      const currentTier = getUserTier(userData);
      if (previousTierRef.current === 'free' && currentTier !== 'free') {
        // User was upgraded from free tier
        onOpenUpgradeSuccess();
      }
      previousTierRef.current = currentTier;
    }
  }, [userData, onOpenUpgradeSuccess]);

  // Shared-session bootstrap. A tab with nothing in storage may still belong to
  // someone who signed in on the marketing site (www.importjdmmagic.com): the
  // API set a cookie for the whole importjdmmagic.com domain when they did.
  // Ask the API once; if it answers with a session, adopt it exactly as a fresh
  // login would. A 401 here is the normal signed-out answer, not an error.
  //
  // Session storage only (rememberMe=false): the cookie itself carries the
  // long-lived persistence, and re-running this on the next cold start is
  // cheaper than guessing which expiry the token was minted with.
  //
  // The same probe also carries sign-outs across: when this tab still holds a
  // token but the cookie is gone (a definitive 401, not a network failure) and
  // this browser is known to support the cookie, the user signed out on the
  // marketing site, so sign out here too.
  const sessionProbeDone = useRef(false);
  useEffect(() => {
    if (sessionProbeDone.current) return;
    sessionProbeDone.current = true;
    let active = true;
    getSession().then((probe) => {
      if (!active) return;
      if (probe.status === 200 && probe.data && probe.data.token) {
        localStorage.setItem(COOKIE_SESSION_FLAG, '1');
        if (!token) {
          console.log("Adopting session from shared cookie");
          loginHandler(probe.data, false);
        }
        return;
      }
      if (probe.status === 401 && token && localStorage.getItem(COOKIE_SESSION_FLAG) === '1') {
        console.log("Shared session ended elsewhere - signing out");
        logoutHandler();
      }
    });
    // The guard above is deliberately never reset: this provider lives for
    // the whole app, and re-probing when `token` changes would race a sign-out
    // (the cookie-clearing request is still in flight when the token clears,
    // so a fresh probe could answer 200 and sign the user straight back in).
    return () => { active = false; };
  }, [token, loginHandler, logoutHandler]);

  // If we have a token but no user data, fetch the user data
  useEffect(() => {
    if (token && !userData) {
      const fetchUserData = async () => {
        try {
          console.log("Fetching user data - token exists but no user data");
          const response = await getUser();
          console.log("getUserData response:", response);
          
          // Check if token exists in localStorage (remember me was checked)
          const hasRememberMe = !!localStorage.getItem('token');
          
          // Validate and extract user data with helper functions
          if (response && response.status === 200) {
            // Try to extract user data from the response
            let extractedUserData = null;
            
            // First check if the data property directly contains user data
            if (isValidUserData(response.data)) {
              extractedUserData = extractUserData(response.data);
            } 
            // If not, check if response.data.data exists (nested structure)
            else if (response.data && response.data.data && isValidUserData(response.data.data)) {
              extractedUserData = extractUserData(response.data.data);
            }
            
            if (extractedUserData) {
              console.log("User data successfully extracted:", extractedUserData);
              previousTierRef.current = getUserTier(extractedUserData);
              setUserData(extractedUserData);
              const userDataStr = JSON.stringify(extractedUserData);
              
              // Always store in sessionStorage
              sessionStorage.setItem('userData', userDataStr);
              
              // Only store in localStorage if remember me was checked
              if (hasRememberMe) {
                localStorage.setItem('userData', userDataStr);
              }
            } else {
              console.error("Failed to extract valid user data from response:", response);
              logoutHandler();
            }
          } else {
            console.error("Failed to get valid user data:", response);
            logoutHandler();
          }
        } catch (error) {
          console.error('Failed to fetch user data:', error);
          logoutHandler();
        }
      };

      fetchUserData();
    }
  }, [token, userData, logoutHandler]);

  // Implement a polling mechanism to check for tier changes periodically
  useEffect(() => {
    let intervalId;

    if (userIsLoggedIn && userData) {
      const checkForChanges = async () => {
        try {
          const response = await getUser();
          
          // Check if token exists in localStorage (remember me was checked)
          const hasRememberMe = !!localStorage.getItem('token');
          
          // Validate and extract user data with helper functions
          if (response?.status === 200 && isValidUserData(response.data)) {
            const newUserData = extractUserData(response.data);
            
            // Check if isVerified status changed
            if (newUserData.isVerified !== userData.isVerified || newUserData.isVipPlus !== userData.isVipPlus) {
              console.log("User tier changed - updating state");
              // User tier has changed, update the userData
              setUserData(newUserData);
              const userDataStr = JSON.stringify(newUserData);
              sessionStorage.setItem('userData', userDataStr);
              
              // Only update localStorage if remember me was checked
              if (hasRememberMe) {
                localStorage.setItem('userData', userDataStr);
              }
            }
            
            // Check if deposit amount changed
            if (newUserData.deposit?.amount !== userData.deposit?.amount) {
              console.log("Deposit amount changed - updating state");
              // Deposit amount updated, update userData
              setUserData(newUserData);
              const userDataStr = JSON.stringify(newUserData);
              sessionStorage.setItem('userData', userDataStr);
              
              // Only update localStorage if remember me was checked
              if (hasRememberMe) {
                localStorage.setItem('userData', userDataStr);
              }
            }
          }
        } catch (error) {
          console.error('Error checking for tier updates:', error);
        }
      };

      // A stored copy with no deposit is a login response saved by an older build. It
      // would read as a $0 bid limit, so replace it now rather than in a minute.
      if (!userData.deposit) {
        checkForChanges();
      }

      intervalId = setInterval(checkForChanges, 60000); // Check every minute
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [userIsLoggedIn, userData]);

  // For a page that has just saved part of the user record (saved searches, say): keeps
  // the shared copy in step, so other pages and a return visit don't show the old value.
  const patchUserData = useCallback((fields) => {
    setUserData((current) => {
      if (!current) return current;
      const next = { ...current, ...fields };
      const userDataStr = JSON.stringify(next);
      sessionStorage.setItem('userData', userDataStr);
      if (localStorage.getItem('token')) {
        localStorage.setItem('userData', userDataStr);
      }
      return next;
    });
  }, []);

  const contextValue = {
    token,
    isLoggedIn: userIsLoggedIn,
    login: loginHandler,
    logout: logoutHandler,
    isRedirected,
    redirection: redirectionHandler,
    userData,
    userTier,
    isFreeTier,
    isVipPlus,
    closeUpgradeSuccess: onCloseUpgradeSuccess,
    patchUserData
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
      <UpgradeSuccessModal 
        isOpen={isUpgradeSuccessOpen} 
        onClose={onCloseUpgradeSuccess} 
        depositAmount={userData?.deposit?.amount || 0}
      />
    </AuthContext.Provider>
  );
};

export default AuthContext;

